import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useSEO from '@/hooks/useSEO';

// ─── Types ────────────────────────────────────────────────────────────────────

interface LetterheadData {
  recipientName: string;
  recipientTitle: string;
  recipientCompany: string;
  recipientAddress: string;
  recipientCity: string;
  date: string;
  subject: string;
  salutation: string;
  body: string;
  closing: string;
  senderName: string;
  senderTitle: string;
  senderEmail: string;
  senderPhone: string;
  referenceNumber: string;
}

type DesignVariant = 'classic' | 'modern' | 'minimal' | 'executive';

interface Variant {
  id: DesignVariant;
  label: string;
  description: string;
  headerBg: string;
  headerText: string;
  accentColor: string;
  footerBg: string;
  footerText: string;
  borderColor: string;
  bodyBg: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const VARIANTS: Variant[] = [
  {
    id: 'classic',
    label: 'Classic Dark',
    description: 'Dark header with gold accents',
    headerBg: '#1A1A1A',
    headerText: '#FFFFFF',
    accentColor: '#C9A96E',
    footerBg: '#1A1A1A',
    footerText: '#FFFFFF',
    borderColor: '#C9A96E',
    bodyBg: '#FFFFFF',
  },
  {
    id: 'modern',
    label: 'Modern Cream',
    description: 'Cream background, charcoal type',
    headerBg: '#F7F4EF',
    headerText: '#1A1A1A',
    accentColor: '#8B7355',
    footerBg: '#F0EDE6',
    footerText: '#6B6B6B',
    borderColor: '#C9A96E',
    bodyBg: '#FDFCFA',
  },
  {
    id: 'minimal',
    label: 'Minimal White',
    description: 'Clean white with gold rule',
    headerBg: '#FFFFFF',
    headerText: '#1A1A1A',
    accentColor: '#C9A96E',
    footerBg: '#FAFAF8',
    footerText: '#9A9A9A',
    borderColor: '#E8E6E3',
    bodyBg: '#FFFFFF',
  },
  {
    id: 'executive',
    label: 'Executive Gold',
    description: 'Gold header, premium feel',
    headerBg: '#C9A96E',
    headerText: '#1A1A1A',
    accentColor: '#1A1A1A',
    footerBg: '#2C2C2C',
    footerText: '#C9A96E',
    borderColor: '#C9A96E',
    bodyBg: '#FFFFFF',
  },
];

const DEFAULT_DATA: LetterheadData = {
  recipientName: 'Mr. Jonathan Whitfield',
  recipientTitle: 'Managing Director',
  recipientCompany: 'Whitfield Capital Partners',
  recipientAddress: '88 Knightsbridge, Suite 400',
  recipientCity: 'London, SW1X 7LJ',
  date: 'April 5, 2026',
  subject: 'Quarterly Property Performance Review — Q1 2026',
  salutation: 'Dear Mr. Whitfield,',
  body: `We are pleased to present the Q1 2026 performance summary for your portfolio under Higher Standard Global management. This quarter has demonstrated continued strength across all three properties, with occupancy rates maintained at 100% and rental income exceeding projections by 8.3%.

The Mayfair residence has been fully occupied throughout the quarter with the current tenants expressing interest in a lease extension. We have initiated preliminary discussions and will present formal renewal terms for your consideration within the next ten days.

The Kensington property underwent the scheduled HVAC maintenance in February, completed on time and within the approved budget. All systems are now operating at optimal efficiency ahead of the summer season.

We remain committed to the highest standards of management and discretion on your behalf. Please do not hesitate to contact us should you wish to discuss any aspect of this report or your portfolio in greater detail.`,
  closing: 'Yours sincerely,',
  senderName: 'Hector',
  senderTitle: 'Principal & Managing Director',
  senderEmail: 'Hector.S@higherstandardglobal.com',
  senderPhone: '+1 (307) 218 3610',
  referenceNumber: 'HSG-2026-Q1-0042',
};

const FIELD_GROUPS = [
  {
    label: 'Letter',
    fields: [
      { key: 'date', label: 'Date', type: 'text', placeholder: 'April 5, 2026' },
      { key: 'referenceNumber', label: 'Reference No.', type: 'text', placeholder: 'HSG-2026-001' },
      { key: 'subject', label: 'Subject Line', type: 'text', placeholder: 'Re: Property Management Update' },
    ],
  },
  {
    label: 'Recipient',
    fields: [
      { key: 'recipientName', label: 'Name', type: 'text', placeholder: 'Mr. Jonathan Whitfield' },
      { key: 'recipientTitle', label: 'Title', type: 'text', placeholder: 'Managing Director' },
      { key: 'recipientCompany', label: 'Company', type: 'text', placeholder: 'Whitfield Capital Partners' },
      { key: 'recipientAddress', label: 'Address', type: 'text', placeholder: '88 Knightsbridge, Suite 400' },
      { key: 'recipientCity', label: 'City / Postcode', type: 'text', placeholder: 'London, SW1X 7LJ' },
    ],
  },
  {
    label: 'Body',
    fields: [
      { key: 'salutation', label: 'Salutation', type: 'text', placeholder: 'Dear Mr. Whitfield,' },
      { key: 'body', label: 'Body Text', type: 'textarea', placeholder: 'Letter body...' },
      { key: 'closing', label: 'Closing', type: 'text', placeholder: 'Yours sincerely,' },
    ],
  },
  {
    label: 'Sender',
    fields: [
      { key: 'senderName', label: 'Name', type: 'text', placeholder: 'Hector' },
      { key: 'senderTitle', label: 'Title', type: 'text', placeholder: 'Principal & Managing Director' },
      { key: 'senderEmail', label: 'Email', type: 'text', placeholder: 'Hector.S@higherstandardglobal.com' },
      { key: 'senderPhone', label: 'Phone', type: 'text', placeholder: '+1 (307) 218 3610' },
    ],
  },
];

// ─── Variant Swatch ───────────────────────────────────────────────────────────

function VariantSwatch({ variant, active, onClick }: { variant: Variant; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left rounded-lg border-2 transition-all cursor-pointer overflow-hidden ${
        active ? 'border-[#C9A96E]' : 'border-[#E8E6E3] hover:border-[#C9A96E]/50'
      }`}
    >
      <div className="h-10 w-full flex items-center px-3 gap-2" style={{ backgroundColor: variant.headerBg }}>
        <span className="font-['Cormorant_Garamond'] font-semibold text-sm tracking-widest" style={{ color: variant.headerText === '#FFFFFF' ? '#FFFFFF' : variant.accentColor }}>HSG</span>
        <div className="w-px h-4 opacity-30" style={{ backgroundColor: variant.headerText }}></div>
        <div className="flex flex-col gap-0.5">
          <span className="text-[6px] uppercase tracking-widest font-medium" style={{ color: variant.headerText }}>Higher Standard</span>
          <span className="text-[6px] uppercase tracking-widest font-light" style={{ color: variant.accentColor }}>Global</span>
        </div>
        {/* Gold shimmer bar */}
        <div className="ml-auto h-0.5 w-8 rounded-full" style={{ background: `linear-gradient(to right, ${variant.accentColor}, transparent)` }} />
      </div>
      <div className="px-3 py-2 bg-white">
        <p className="text-xs font-medium text-[#1A1A1A]">{variant.label}</p>
        <p className="text-[10px] text-[#9A9A9A]">{variant.description}</p>
      </div>
    </button>
  );
}

// ─── Editor Field ─────────────────────────────────────────────────────────────

function EditorField({
  label, value, fieldKey, type, placeholder, onChange,
}: {
  label: string; value: string; fieldKey: string; type: string; placeholder: string;
  onChange: (key: keyof LetterheadData, value: string) => void;
}) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-[#9A9A9A] mb-1 font-medium">{label}</label>
      {type === 'textarea' ? (
        <textarea
          value={value}
          onChange={(e) => onChange(fieldKey as keyof LetterheadData, e.target.value)}
          placeholder={placeholder}
          rows={8}
          className="w-full text-xs text-[#2C2C2C] bg-[#FAFAF8] border border-[#E8E6E3] rounded-md px-3 py-2.5 resize-none focus:outline-none focus:border-[#C9A96E] transition-colors leading-relaxed"
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(fieldKey as keyof LetterheadData, e.target.value)}
          placeholder={placeholder}
          className="w-full text-xs text-[#2C2C2C] bg-[#FAFAF8] border border-[#E8E6E3] rounded-md px-3 py-2 focus:outline-none focus:border-[#C9A96E] transition-colors"
        />
      )}
    </div>
  );
}

// ─── Letterhead Preview ───────────────────────────────────────────────────────

function LetterheadPreview({ data, variant }: { data: LetterheadData; variant: Variant }) {
  const A4_W = 794;
  const A4_H = 1123;
  const isDarkHeader = variant.headerBg === '#1A1A1A' || variant.headerBg === '#2C2C2C';
  const isGoldHeader = variant.headerBg === '#C9A96E';

  return (
    <div
      style={{
        width: A4_W,
        height: A4_H,
        fontFamily: 'Inter, sans-serif',
        backgroundColor: variant.bodyBg,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* ── Header ── */}
      <div
        style={{
          backgroundColor: variant.headerBg,
          padding: '28px 48px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Corner accent top-right */}
        <div style={{
          position: 'absolute', top: 0, right: 0,
          width: 60, height: 60,
          borderBottom: `1px solid ${isDarkHeader ? 'rgba(201,169,110,0.25)' : 'rgba(26,26,26,0.15)'}`,
          borderLeft: `1px solid ${isDarkHeader ? 'rgba(201,169,110,0.25)' : 'rgba(26,26,26,0.15)'}`,
          borderBottomLeftRadius: 40,
        }} />

        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{
            fontFamily: '\'Cormorant Garamond\', serif',
            fontWeight: 600, fontSize: 26,
            letterSpacing: '0.15em',
            color: isDarkHeader ? '#C9A96E' : isGoldHeader ? '#1A1A1A' : '#2C2C2C',
            lineHeight: 1,
          }}>HSG</span>
          <div style={{ width: 1, height: 28, backgroundColor: variant.headerText, opacity: 0.2 }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={{
              fontSize: 7.5, letterSpacing: '0.22em', fontWeight: 500,
              textTransform: 'uppercase',
              color: isDarkHeader ? '#C9A96E' : variant.headerText,
              lineHeight: 1,
            }}>Higher Standard</span>
            <span style={{
              fontSize: 7.5, letterSpacing: '0.35em', fontWeight: 300,
              textTransform: 'uppercase',
              color: isDarkHeader ? 'rgba(255,255,255,0.55)' : isGoldHeader ? 'rgba(26,26,26,0.6)' : '#8B7355',
              lineHeight: 1,
            }}>Global</span>
          </div>
        </div>

        {/* Right: contact + tagline */}
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontSize: 7.5, color: variant.headerText, opacity: 0.55, letterSpacing: '0.04em', marginBottom: 3 }}>
            higherstandardglobal.com
          </p>
          <p style={{ fontSize: 7.5, color: variant.headerText, opacity: 0.55, letterSpacing: '0.04em', marginBottom: 5 }}>
            {data.senderPhone || '+1 (307) 218 3610'}
          </p>
          <p style={{
            fontSize: 7, color: isDarkHeader ? '#C9A96E' : isGoldHeader ? '#1A1A1A' : '#8B7355',
            letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 500,
          }}>By Reference Only</p>
        </div>
      </div>

      {/* Gold shimmer rule */}
      <div style={{
        height: 2,
        background: `linear-gradient(to right, ${variant.borderColor} 0%, ${variant.borderColor}88 60%, transparent 100%)`,
      }} />

      {/* ── Body ── */}
      <div style={{ padding: '36px 48px 100px', position: 'relative' }}>

        {/* Date + Reference */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
          <p style={{ fontSize: 8.5, color: '#6B6B6B', letterSpacing: '0.04em' }}>{data.date}</p>
          {data.referenceNumber && (
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: 6.5, color: '#9A9A9A', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 2 }}>Reference</p>
              <p style={{ fontSize: 8.5, color: '#2C2C2C', fontFamily: 'monospace', letterSpacing: '0.04em' }}>{data.referenceNumber}</p>
            </div>
          )}
        </div>

        {/* Recipient */}
        <div style={{ marginBottom: 28 }}>
          <p style={{ fontSize: 9.5, fontWeight: 600, color: '#1A1A1A', marginBottom: 2 }}>{data.recipientName}</p>
          {data.recipientTitle && <p style={{ fontSize: 8.5, color: '#6B6B6B', marginBottom: 1 }}>{data.recipientTitle}</p>}
          {data.recipientCompany && <p style={{ fontSize: 8.5, color: '#6B6B6B', marginBottom: 1 }}>{data.recipientCompany}</p>}
          {data.recipientAddress && <p style={{ fontSize: 8.5, color: '#6B6B6B', marginBottom: 1 }}>{data.recipientAddress}</p>}
          {data.recipientCity && <p style={{ fontSize: 8.5, color: '#6B6B6B' }}>{data.recipientCity}</p>}
        </div>

        {/* Subject */}
        {data.subject && (
          <div style={{ marginBottom: 22 }}>
            <p style={{ fontSize: 6.5, color: '#9A9A9A', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 5 }}>Re:</p>
            <p style={{
              fontSize: 12, fontWeight: 600, color: '#1A1A1A',
              fontFamily: '\'Cormorant Garamond\', serif', letterSpacing: '0.02em',
            }}>{data.subject}</p>
            {/* Short gold rule under subject */}
            <div style={{ height: 1.5, width: 40, backgroundColor: variant.borderColor, marginTop: 7, borderRadius: 1 }} />
          </div>
        )}

        {/* Salutation */}
        <p style={{ fontSize: 9.5, color: '#2C2C2C', marginBottom: 14, letterSpacing: '0.01em' }}>{data.salutation}</p>

        {/* Body paragraphs */}
        <div style={{ marginBottom: 22 }}>
          {data.body.split('\n\n').map((para, i) => (
            <p key={i} style={{ fontSize: 9, color: '#4A4A4A', lineHeight: 1.75, marginBottom: 11, letterSpacing: '0.01em' }}>
              {para}
            </p>
          ))}
        </div>

        {/* Closing */}
        <p style={{ fontSize: 9.5, color: '#2C2C2C', marginBottom: 36, letterSpacing: '0.01em' }}>{data.closing}</p>

        {/* Signature block */}
        <div>
          <div style={{ width: 100, height: 1, backgroundColor: '#E8E6E3', marginBottom: 8 }} />
          <p style={{ fontSize: 10, fontWeight: 700, color: '#1A1A1A', marginBottom: 2, fontFamily: '\'Cormorant Garamond\', serif', letterSpacing: '0.03em' }}>{data.senderName}</p>
          <p style={{ fontSize: 8, color: '#8B7355', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>{data.senderTitle}</p>
          <p style={{ fontSize: 7.5, color: '#8B7355', marginBottom: 1 }}>{data.senderEmail}</p>
          <p style={{ fontSize: 7.5, color: '#9A9A9A' }}>{data.senderPhone}</p>
        </div>

        {/* Watermark HSG — bottom right of body, very subtle */}
        <div style={{
          position: 'absolute', bottom: 110, right: 48,
          fontFamily: '\'Cormorant Garamond\', serif',
          fontSize: 72, fontWeight: 600,
          letterSpacing: '0.15em',
          color: variant.borderColor,
          opacity: 0.04,
          lineHeight: 1,
          userSelect: 'none',
          pointerEvents: 'none',
        }}>HSG</div>
      </div>

      {/* ── Footer ── */}
      <div
        style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          backgroundColor: variant.footerBg,
          padding: '12px 48px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderTop: `1.5px solid ${variant.borderColor}`,
        }}
      >
        {/* Left: company info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            fontFamily: '\'Cormorant Garamond\', serif',
            fontSize: 11, fontWeight: 600, letterSpacing: '0.12em',
            color: variant.id === 'executive' ? '#C9A96E' : variant.id === 'classic' ? '#C9A96E' : '#8B7355',
          }}>HSG</span>
          <div style={{ width: 1, height: 12, backgroundColor: variant.footerText, opacity: 0.2 }} />
          <p style={{ fontSize: 6.5, color: variant.footerText, opacity: 0.55, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            Higher Standard Global · Registered in England &amp; Wales
          </p>
        </div>
        {/* Right: confidential */}
        <p style={{
          fontSize: 6.5,
          color: variant.id === 'executive' ? '#C9A96E' : variant.accentColor,
          letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 500,
        }}>Confidential</p>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function LetterheadPage() {
  useSEO({
    title: 'Letterhead Templates | Higher Standard Global',
    description: 'Print-ready A4 letterhead templates for HSG formal correspondence.',
    canonical: '/letterhead',
    noIndex: true,
  });

  const navigate = useNavigate();
  const [data, setData] = useState<LetterheadData>(DEFAULT_DATA);
  const [activeVariant, setActiveVariant] = useState<DesignVariant>('classic');
  const [activeGroup, setActiveGroup] = useState(0);
  const [showPrintTips, setShowPrintTips] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const variant = VARIANTS.find((v) => v.id === activeVariant) ?? VARIANTS[0];

  const handleChange = (key: keyof LetterheadData, value: string) => {
    setData((prev) => ({ ...prev, [key]: value }));
  };

  const handlePrint = () => window.print();

  const PREVIEW_CONTAINER_W = 520;
  const A4_W = 794;
  const previewScale = PREVIEW_CONTAINER_W / A4_W;

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #print-area, #print-area * { visibility: visible !important; }
          #print-area {
            position: fixed !important;
            top: 0 !important; left: 0 !important;
            width: 210mm !important; height: 297mm !important;
            transform: none !important;
          }
        }
        @keyframes shimmerSlide {
          0% { left: -60%; }
          60% { left: 110%; }
          100% { left: 110%; }
        }
      `}</style>

      <div className="min-h-screen bg-[#F7F4EF]">

        {/* ── Hero ── */}
        <div className="w-full bg-[#1A1A1A] pt-16 pb-12 px-6 md:px-16 relative overflow-hidden">
          {/* Animated shimmer line — matches business card */}
          <div className="absolute top-0 left-0 right-0 overflow-hidden" style={{ height: 2 }}>
            <div style={{
              position: 'absolute', top: 0, left: '-60%', width: '50%', height: '100%',
              background: 'linear-gradient(90deg, transparent 0%, rgba(201,169,110,0.9) 40%, rgba(255,220,140,1) 50%, rgba(201,169,110,0.9) 60%, transparent 100%)',
              animation: 'shimmerSlide 4s ease-in-out infinite',
            }} />
          </div>
          {/* Corner accent */}
          <div className="absolute top-0 right-0" style={{ width: 80, height: 80, borderBottom: '1px solid rgba(201,169,110,0.2)', borderLeft: '1px solid rgba(201,169,110,0.2)', borderBottomLeftRadius: 60 }} />

          <div className="max-w-6xl mx-auto">
            <p className="text-xs uppercase tracking-[0.35em] text-[#C9A96E] mb-3 font-medium">Higher Standard Global</p>
            <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-white mb-3 leading-tight">
              Letterhead Templates
            </h1>
            <p className="text-sm text-white/40 max-w-lg leading-relaxed">
              Print-ready A4 letterhead for formal correspondence. Edit content, choose a design, and print directly from the browser.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-6">
              <button onClick={handlePrint}
                className="flex items-center gap-2 text-xs px-4 py-2.5 rounded-md bg-[#C9A96E] text-[#1A1A1A] font-medium hover:bg-[#B8956A] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-printer-line"></i>Print / Save as PDF
              </button>
              <a href="/business-card" onClick={(e) => { e.preventDefault(); navigate('/business-card'); }}
                className="flex items-center gap-2 text-xs px-4 py-2.5 rounded-md border border-white/20 text-white/60 hover:border-white/40 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-contacts-line"></i>Business Card
              </a>
              <a href="/brand" onClick={(e) => { e.preventDefault(); navigate('/brand'); }}
                className="flex items-center gap-2 text-xs px-4 py-2.5 rounded-md border border-white/20 text-white/60 hover:border-white/40 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-palette-line"></i>Brand Guidelines
              </a>
              <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }}
                className="flex items-center gap-2 text-xs px-4 py-2.5 rounded-md border border-white/20 text-white/60 hover:border-white/40 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>Back to site
              </a>
            </div>
          </div>
        </div>

        {/* ── Main Layout ── */}
        <div className="max-w-6xl mx-auto px-6 md:px-16 py-10">
          <div className="flex flex-col lg:flex-row gap-8">

            {/* ── Left: Editor ── */}
            <div className="w-full lg:w-[340px] flex-shrink-0 space-y-5">

              {/* Design Variants */}
              <div className="bg-white rounded-lg border border-[#E8E6E3] p-5">
                <p className="text-xs uppercase tracking-[0.25em] text-[#9A9A9A] mb-4 font-medium">Design Variant</p>
                <div className="grid grid-cols-2 gap-3">
                  {VARIANTS.map((v) => (
                    <VariantSwatch key={v.id} variant={v} active={activeVariant === v.id} onClick={() => setActiveVariant(v.id)} />
                  ))}
                </div>
              </div>

              {/* Field Editor */}
              <div className="bg-white rounded-lg border border-[#E8E6E3] overflow-hidden">
                <div className="flex border-b border-[#E8E6E3] overflow-x-auto">
                  {FIELD_GROUPS.map((group, i) => (
                    <button key={group.label} onClick={() => setActiveGroup(i)}
                      className={`px-4 py-3 text-[10px] uppercase tracking-[0.15em] font-medium whitespace-nowrap transition-colors cursor-pointer flex-shrink-0 ${
                        activeGroup === i ? 'text-[#1A1A1A] border-b-2 border-[#C9A96E] bg-[#FAFAF8]' : 'text-[#9A9A9A] hover:text-[#6B6B6B]'
                      }`}
                    >{group.label}</button>
                  ))}
                </div>
                <div className="p-5 space-y-4">
                  {FIELD_GROUPS[activeGroup].fields.map((field) => (
                    <EditorField
                      key={field.key} label={field.label}
                      value={data[field.key as keyof LetterheadData]}
                      fieldKey={field.key} type={field.type} placeholder={field.placeholder}
                      onChange={handleChange}
                    />
                  ))}
                </div>
              </div>

              {/* Print Tips */}
              <div className="bg-white rounded-lg border border-[#E8E6E3] overflow-hidden">
                <button onClick={() => setShowPrintTips(!showPrintTips)}
                  className="w-full flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-[#FAFAF8] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <i className="ri-printer-line text-sm text-[#8B7355]"></i>
                    <p className="text-xs font-medium text-[#1A1A1A]">Print Instructions</p>
                  </div>
                  <i className={`text-xs text-[#9A9A9A] transition-transform ${showPrintTips ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                </button>
                {showPrintTips && (
                  <div className="px-5 pb-5 space-y-3 border-t border-[#E8E6E3]">
                    {[
                      'Click "Print / Save as PDF" above',
                      'Set paper size to A4 (210 × 297 mm)',
                      'Set margins to None or Minimum',
                      'Enable "Background graphics" for colored headers',
                      'Choose "Save as PDF" for a digital file',
                      'For physical print: use 100gsm bond paper minimum',
                    ].map((text, i) => (
                      <div key={i} className="flex items-start gap-3 pt-3">
                        <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full bg-[#C9A96E]/15 text-[#8B7355] text-[10px] font-semibold">{i + 1}</span>
                        <p className="text-xs text-[#6B6B6B] leading-relaxed">{text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Spec table */}
              <div className="bg-white rounded-lg border border-[#E8E6E3] p-5">
                <p className="text-xs uppercase tracking-[0.25em] text-[#9A9A9A] mb-4 font-medium">Print Specifications</p>
                <div className="space-y-1.5">
                  {[
                    { label: 'Format', value: 'A4 (210 × 297 mm)' },
                    { label: 'Orientation', value: 'Portrait' },
                    { label: 'Margins', value: '20mm all sides' },
                    { label: 'Paper weight', value: '100–120 gsm' },
                    { label: 'Paper finish', value: 'Uncoated bond' },
                    { label: 'Color mode', value: 'CMYK' },
                    { label: 'Resolution', value: '300 dpi minimum' },
                  ].map((row) => (
                    <div key={row.label} className="flex items-center justify-between py-1.5 border-b border-[#F7F4EF] last:border-0">
                      <p className="text-[11px] text-[#6B6B6B]">{row.label}</p>
                      <code className="text-[11px] text-[#8B7355] bg-[#F7F4EF] px-2 py-0.5 rounded font-mono">{row.value}</code>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Right: Preview ── */}
            <div className="flex-1 min-w-0">

              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-[#9A9A9A] font-medium">Live Preview</p>
                  <p className="text-[10px] text-[#9A9A9A] mt-0.5">A4 · {variant.label}</p>
                </div>
                <button onClick={handlePrint}
                  className="flex items-center gap-2 text-xs px-4 py-2 rounded-md bg-[#2C2C2C] text-white hover:bg-[#C9A96E] hover:text-[#1A1A1A] transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-printer-line"></i>Print / Save PDF
                </button>
              </div>

              {/* A4 preview container */}
              <div
                className="relative bg-white rounded-lg border border-[#E8E6E3] overflow-hidden"
                style={{ width: PREVIEW_CONTAINER_W, height: Math.round(1123 * previewScale), maxWidth: '100%' }}
              >
                <div
                  id="print-area"
                  ref={printRef}
                  style={{ width: A4_W, height: 1123, transform: `scale(${previewScale})`, transformOrigin: 'top left' }}
                >
                  <LetterheadPreview data={data} variant={variant} />
                </div>
              </div>

              {/* Variant quick-switch */}
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <p className="text-[10px] text-[#9A9A9A] uppercase tracking-widest mr-1">Quick switch:</p>
                {VARIANTS.map((v) => (
                  <button key={v.id} onClick={() => setActiveVariant(v.id)}
                    className={`flex items-center gap-1.5 text-[10px] px-3 py-1.5 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                      activeVariant === v.id
                        ? 'border-[#C9A96E] bg-[#C9A96E]/10 text-[#8B7355] font-medium'
                        : 'border-[#E8E6E3] text-[#9A9A9A] hover:border-[#C9A96E]/50 hover:text-[#6B6B6B]'
                    }`}
                  >
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: v.headerBg, border: v.headerBg === '#FFFFFF' ? '1px solid #E8E6E3' : 'none' }} />
                    {v.label}
                  </button>
                ))}
              </div>

              {/* Usage guidelines */}
              <div className="mt-5 bg-white rounded-lg border border-[#E8E6E3] p-5">
                <p className="text-xs uppercase tracking-[0.25em] text-[#9A9A9A] mb-3 font-medium">Usage Guidelines</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { icon: 'ri-checkbox-circle-line', color: 'text-[#4A7C59]', text: 'Use Classic Dark for premium client correspondence' },
                    { icon: 'ri-checkbox-circle-line', color: 'text-[#4A7C59]', text: 'Use Minimal White for internal memos and reports' },
                    { icon: 'ri-checkbox-circle-line', color: 'text-[#4A7C59]', text: 'Always print on uncoated bond paper (100gsm+)' },
                    { icon: 'ri-checkbox-circle-line', color: 'text-[#4A7C59]', text: 'Enable background graphics in print settings' },
                    { icon: 'ri-close-circle-line', color: 'text-[#C0392B]', text: 'Do not alter the logo or brand colors' },
                    { icon: 'ri-close-circle-line', color: 'text-[#C0392B]', text: 'Do not use on glossy or coated paper stock' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <i className={`${item.icon} ${item.color} text-sm flex-shrink-0 mt-0.5`}></i>
                      <p className="text-xs text-[#6B6B6B] leading-relaxed">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Related tools */}
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { label: 'Business Card', icon: 'ri-contacts-line', path: '/business-card', desc: 'Print-ready card designer' },
                  { label: 'Brand Guidelines', icon: 'ri-palette-line', path: '/brand', desc: 'Colors, type, spacing' },
                  { label: 'Logo Export', icon: 'ri-download-line', path: '/logo-export', desc: 'SVG, PNG, favicon' },
                ].map((tool) => (
                  <a key={tool.path} href={tool.path}
                    onClick={(e) => { e.preventDefault(); navigate(tool.path); }}
                    className="flex items-center gap-3 bg-white rounded-lg border border-[#E8E6E3] px-4 py-3 hover:border-[#C9A96E]/50 transition-colors cursor-pointer group"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-md bg-[#F7F4EF] group-hover:bg-[#C9A96E]/10 transition-colors flex-shrink-0">
                      <i className={`${tool.icon} text-sm text-[#8B7355]`}></i>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-[#1A1A1A]">{tool.label}</p>
                      <p className="text-[10px] text-[#9A9A9A]">{tool.desc}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
